import LoadingSpinner from '@/shared/ui/LoadingSpinner/ui/LoadingSpinner';
import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';

interface LoadingSpinnerContextType {
  showLoading: (message?: string, size?: 'small' | 'medium' | 'large') => void;
  hideLoading: () => void;
  isLoading: boolean;
}

const LoadingSpinnerContext = createContext<LoadingSpinnerContextType | undefined>(undefined);

interface LoadingSpinnerProviderProps {
  children: ReactNode;
}

export const LoadingSpinnerProvider: React.FC<LoadingSpinnerProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string>('잠시만 기다려주세요...');
  const [size, setSize] = useState<'small' | 'medium' | 'large'>('medium');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 10초 강제 타임아웃 설정
  useEffect(() => {
    if (isLoading) {
      timeoutRef.current = setTimeout(() => {
        setIsLoading(false);
      }, 10000); // 10초
    } else {
      // 로딩이 끝나면 타이머 정리
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isLoading]);

  // 브라우저 앞으로가기/뒤로가기 감지하여 로딩 종료
  useEffect(() => {
    const handlePopState = () => {
      if (isLoading) {
        setIsLoading(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isLoading]);

  const showLoading = (msg?: string, spinnerSize?: 'small' | 'medium' | 'large') => {
    if (msg) setMessage(msg);
    if (spinnerSize) setSize(spinnerSize);
    setIsLoading(true);
  };

  const hideLoading = () => {
    setIsLoading(false);
  };

  const handleClose = () => {
    setIsLoading(false);
  };

  return (
    <LoadingSpinnerContext.Provider value={{ showLoading, hideLoading, isLoading }}>
      {children}
      <LoadingSpinner
        show={isLoading}
        message={message}
        size={size}
        fullscreen={true}
        onClose={handleClose}
      />
    </LoadingSpinnerContext.Provider>
  );
};

export const useLoadingSpinner = (): LoadingSpinnerContextType => {
  const context = useContext(LoadingSpinnerContext);
  if (!context) {
    throw new Error('useLoadingSpinner는 LoadingSpinnerProvider 내에서 사용되어야 합니다.');
  }
  return context;
}; 